/**
 * Make an SVG self-contained by inlining the faces it references as base64
 * `@font-face` rules.
 *
 * WHY: the app renders SVG previews through an `<img>` and lets users download
 * the raw `.svg`. An `<img>`-hosted SVG is an isolated document — it does NOT
 * inherit the page's loaded FontFaces, and a downloaded file has no access to
 * the app's `/fonts/**` server (or the authenticated `/assets/**` endpoint) at
 * all. Without embedding, any registry font (Rubik, Oswald, …) OR tenant brand
 * font silently falls back to a system face, so the preview and the downloaded
 * asset disagree with the editor. Inlining the faces as data: URIs makes the
 * SVG paint the intended glyphs anywhere.
 *
 * Face sources come from getFontFaceInfo(), which returns either PUBLIC static
 * paths (registry woff2) or AUTHED asset URLs (tenant fonts) — flagged per
 * face. Public faces fetch bare; authed faces carry the Bearer header. For
 * registry fonts we only embed weights 400/700 that applySvgFields can emit;
 * tenant fonts embed every uploaded weight. Fetches are cached module-wide and
 * failures degrade silently to the font-family fallback stack.
 */

import { SUPPORTED_FONTS, getFontFaceInfo, type FontFaceSource } from "./fonts";
import { getTenantFamilies } from "./tenantFonts";
import { apiUrl } from "@/api/client";
import { useAuthStore } from "@/stores/auth";

/** Weights applySvgFields can produce via `{{key.fontWeight}}` (400 | 700).
 *  Only gates PUBLIC registry faces — tenant faces embed as-uploaded. */
const EMBED_WEIGHTS: readonly number[] = [400, 700];

/** source-url → base64 (or null on failure). Promise-cached so repeated
 *  previews of the same graphic never refetch a face. Keyed by authed-prefix +
 *  url so a public and authed URL never collide. */
const faceCache = new Map<string, Promise<string | null>>();

/** Encode an ArrayBuffer as base64 without blowing the call stack on large
 *  font files (String.fromCharCode(...bytes) overflows for big arrays). */
function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** data: URI mime for a CSS format keyword. */
function mimeForFormat(format: string): string {
  switch (format) {
    case "woff":
      return "font/woff";
    case "truetype":
      return "font/ttf";
    case "opentype":
      return "font/otf";
    case "woff2":
    default:
      return "font/woff2";
  }
}

/** Fetch + base64 a single face. Public sources fetch bare; authed (tenant)
 *  sources carry the Bearer header and resolve their URL against the API base.
 *  Returns null (never throws) on any failure so the caller can skip the face. */
function loadFaceBase64(source: FontFaceSource): Promise<string | null> {
  const key = `${source.authed ? "a" : "p"}:${source.url}`;
  let promise = faceCache.get(key);
  if (!promise) {
    promise = (async () => {
      try {
        const headers: Record<string, string> = {};
        let url = source.url;
        if (source.authed) {
          const { token, activeTeamId } = useAuthStore.getState();
          if (token) headers.Authorization = `Bearer ${token}`;
          if (activeTeamId) headers["X-Team-Id"] = activeTeamId;
          url = apiUrl(source.url);
        }
        const res = await fetch(url, { headers });
        if (!res.ok) return null;
        const buf = await res.arrayBuffer();
        return arrayBufferToBase64(buf);
      } catch {
        return null;
      }
    })();
    faceCache.set(key, promise);
  }
  return promise;
}

/** Registry family names, deduped (e.g. "Geist" appears once). */
const REGISTRY_FAMILIES = Array.from(
  new Set(SUPPORTED_FONTS.map((f) => f.family)),
);

/**
 * Return `svg` with an injected `<style>` of base64 `@font-face` rules for
 * every registry OR tenant family it references. If it references none, the
 * input is returned unchanged (fast path). Fetch failures skip that face
 * silently.
 */
export async function embedSvgFonts(svg: string): Promise<string> {
  const candidateFamilies = Array.from(
    new Set([...REGISTRY_FAMILIES, ...getTenantFamilies()]),
  );
  const families = candidateFamilies.filter((family) => svg.includes(family));
  if (families.length === 0) return svg;

  // Gather (family, source) tasks for existing faces, fetch in parallel.
  type Task = { family: string; source: FontFaceSource };
  const tasks: Task[] = [];
  for (const family of families) {
    const info = getFontFaceInfo(family);
    if (!info) continue;
    for (const face of info.faces) {
      // Registry (public) faces: only the 400/700 applySvgFields can emit.
      // Tenant (authed) faces: embed every uploaded weight.
      if (!face.authed && !EMBED_WEIGHTS.includes(face.weight)) continue;
      tasks.push({ family, source: face });
    }
  }
  if (tasks.length === 0) return svg;

  const faces: string[] = [];
  const results = await Promise.all(
    tasks.map((t) =>
      loadFaceBase64(t.source).then((b64) => ({ t, b64 })),
    ),
  );
  for (const { t, b64 } of results) {
    if (!b64) continue;
    const mime = mimeForFormat(t.source.format);
    faces.push(
      `@font-face{font-family:'${t.family}';` +
        `src:url(data:${mime};base64,${b64}) format('${t.source.format}');` +
        `font-weight:${t.source.weight};font-style:${t.source.style || "normal"};}`,
    );
  }
  if (faces.length === 0) return svg;

  // Inject the <style> as the first child of the root <svg>.
  const open = svg.match(/<svg[^>]*>/);
  if (!open || open.index === undefined) return svg;
  const at = open.index + open[0].length;
  const style = `<style>${faces.join("")}</style>`;
  return svg.slice(0, at) + style + svg.slice(at);
}
